// src/components/fraud/BlacklistEntryDialog.js
// Add / Edit dialog using the actual Firestore schema.
// Fields: uid | email | phone | ip | reason | notes | active
// All identifier fields are optional individually — but at least one must be filled.

import React, { useState, useEffect } from 'react';
import { Modal, Form, Row, Col } from 'react-bootstrap';

// ─── Shared input style ───────────────────────────────────────────────────────
const inputStyle = {
  backgroundColor: 'var(--bg-primary)',
  color: 'var(--text-primary)',
  border: '1px solid rgba(255,255,255,0.15)',
  borderRadius: 8,
  fontSize: 13,
};

const labelStyle = {
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--text-secondary)',
  marginBottom: 4,
};

// ─── Default form state ───────────────────────────────────────────────────────
const EMPTY_FORM = {
  uid:    '',
  email:  '',
  phone:  '',
  ip:     '',
  reason: '',
  notes:  '',
  active: true,
};

const entryToForm = (e) => ({
  uid:    e.uid    || '',
  email:  e.email  || '',
  phone:  e.phone  || '',
  ip:     e.ip     || '',
  reason: e.reason || '',
  notes:  e.notes  || '',
  active: e.active !== false,
});

// ─── Identifier field config ─────────────────────────────────────────────────
const IDENTIFIER_FIELDS = [
  { key: 'uid',   label: 'UID',        placeholder: 'Firestore user ID',        color: '#8B5CF6', mono: true  },
  { key: 'email', label: 'Email',      placeholder: 'user@example.com',          color: '#3B82F6', mono: false },
  { key: 'phone', label: 'Phone',      placeholder: '+44 7911 123456',           color: '#22C55E', mono: false },
  { key: 'ip',    label: 'IP Address', placeholder: '192.168.1.1 or CIDR block', color: '#F59E0B', mono: true  },
];

// ─── Main Component ───────────────────────────────────────────────────────────
const BlacklistEntryDialog = ({ open, editEntry, onClose, onSave, opLoading }) => {
  const isEdit = !!editEntry;
  const [form, setForm]     = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm(isEdit ? entryToForm(editEntry) : EMPTY_FORM);
      setErrors({});
    }
  }, [open, editEntry, isEdit]);

  const set = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    if (errors[key]) setErrors((e) => { const n = { ...e }; delete n[key]; return n; });
    // Clear identifier error when any identifier is filled
    if (['uid', 'email', 'phone', 'ip'].includes(key) && val.trim()) {
      setErrors((e) => { const n = { ...e }; delete n._identifiers; return n; });
    }
  };

  // ── Validation ─────────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    const hasIdentifier = ['uid', 'email', 'phone', 'ip'].some((k) => form[k].trim());
    if (!hasIdentifier) errs._identifiers = 'At least one identifier (UID, Email, Phone, or IP) is required';
    if (!form.reason.trim()) errs.reason = 'Reason is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      isEdit ? onSave(editEntry._id, form) : onSave(form);
    }
  };

  return (
    <Modal
      show={open}
      onHide={onClose}
      centered
      backdrop={opLoading ? 'static' : true}
    >
      <div style={{ background: 'var(--bg-surface)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 12, overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--divider)' }}>
          <div>
            <h6 style={{ margin: 0, fontWeight: 700, color: 'var(--text-primary)', fontSize: 15 }}>
              {isEdit ? 'Edit Blacklist Entry' : 'Add to Blacklist'}
            </h6>
            <small style={{ color: 'var(--text-secondary)', fontSize: 11 }}>
              {isEdit ? `Editing: ${editEntry._id}` : 'New entry — fill at least one identifier'}
            </small>
          </div>
          <button
            onClick={onClose} disabled={opLoading}
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid var(--divider)', borderRadius: 6, color: 'var(--text-primary)', width: 32, height: 32, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >×</button>
        </div>

        {/* Body */}
        <div style={{ padding: '24px' }}>
          <Row className="g-3">

            {/* ── Identifiers section header ── */}
            <Col xs={12}>
              <div
                style={{
                  fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1,
                  color: errors._identifiers ? '#EF4444' : 'var(--accent-lime)',
                  borderBottom: `1px solid ${errors._identifiers ? 'rgba(239,68,68,0.3)' : 'var(--divider)'}`,
                  paddingBottom: 6, marginBottom: 2,
                }}
              >
                Identifiers
                <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'var(--text-secondary)', marginLeft: 8 }}>
                  — fill one or more
                </span>
              </div>
              {errors._identifiers && (
                <small style={{ color: '#EF4444', fontSize: 11 }}>{errors._identifiers}</small>
              )}
            </Col>

            {/* ── Each identifier field ── */}
            {IDENTIFIER_FIELDS.map((f) => (
              <Col key={f.key} xs={12} sm={6}>
                <Form.Group>
                  <Form.Label style={{ ...labelStyle, color: form[f.key] ? f.color : 'var(--text-secondary)' }}>
                    {f.label}
                  </Form.Label>
                  <Form.Control
                    id={`bl-dialog-${f.key}`}
                    type={f.key === 'email' ? 'email' : 'text'}
                    placeholder={f.placeholder}
                    value={form[f.key]}
                    onChange={(e) => set(f.key, e.target.value)}
                    style={{
                      ...inputStyle,
                      fontFamily: f.mono ? 'monospace' : 'inherit',
                      borderColor: form[f.key] ? `${f.color}66` : 'rgba(255,255,255,0.12)',
                    }}
                  />
                </Form.Group>
              </Col>
            ))}

            {/* ── Reason ── */}
            <Col xs={12}>
              <div
                style={{
                  fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1,
                  color: 'var(--accent-lime)',
                  borderBottom: '1px solid var(--divider)',
                  paddingBottom: 6, marginBottom: 2, marginTop: 4,
                }}
              >
                Details
              </div>
            </Col>

            <Col xs={12}>
              <Form.Group>
                <Form.Label style={labelStyle}>Reason *</Form.Label>
                <Form.Control
                  id="bl-dialog-reason"
                  type="text"
                  placeholder="Why is this being blacklisted?"
                  value={form.reason}
                  onChange={(e) => set('reason', e.target.value)}
                  style={{ ...inputStyle, borderColor: errors.reason ? '#EF4444' : 'rgba(255,255,255,0.15)' }}
                />
                {errors.reason && <small style={{ color: '#EF4444', fontSize: 11 }}>{errors.reason}</small>}
              </Form.Group>
            </Col>

            <Col xs={12}>
              <Form.Group>
                <Form.Label style={labelStyle}>Notes (optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  id="bl-dialog-notes"
                  placeholder="Any additional context or investigation notes…"
                  value={form.notes}
                  onChange={(e) => set('notes', e.target.value)}
                  style={{ ...inputStyle, resize: 'vertical', minHeight: 64 }}
                />
              </Form.Group>
            </Col>

            {/* ── Active toggle ── */}
            <Col xs={12}>
              <div
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid var(--divider)',
                  borderRadius: 8, padding: '12px 16px',
                }}
              >
                <div>
                  <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 600 }}>Active</div>
                  <small style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                    When active, this entry will be enforced by the fraud engine
                  </small>
                </div>
                <label style={{ position: 'relative', display: 'inline-block', width: 44, height: 24, cursor: 'pointer', flexShrink: 0 }} htmlFor="bl-dialog-active">
                  <input
                    id="bl-dialog-active"
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => set('active', e.target.checked)}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span style={{ position: 'absolute', inset: 0, background: form.active ? '#22C55E' : 'rgba(255,255,255,0.12)', borderRadius: 24, transition: 'background 0.2s' }} />
                  <span style={{ position: 'absolute', top: 3, left: form.active ? 23 : 3, width: 18, height: 18, background: '#fff', borderRadius: '50%', transition: 'left 0.2s' }} />
                </label>
              </div>
            </Col>

          </Row>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 24px', borderTop: '1px solid var(--divider)' }}>
          <button
            onClick={onClose} disabled={opLoading}
            style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, padding: '8px 18px', cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            id="bl-dialog-save-btn"
            onClick={handleSubmit}
            disabled={opLoading}
            style={{ background: 'var(--accent-lime)', border: 'none', borderRadius: 8, color: '#050014', fontSize: 13, fontWeight: 700, padding: '8px 24px', cursor: opLoading ? 'default' : 'pointer', minWidth: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          >
            {opLoading
              ? <span className="admin-loader-sm" style={{ borderTopColor: '#050014' }} />
              : isEdit ? 'Save Changes' : 'Add to Blacklist'
            }
          </button>
        </div>

      </div>
    </Modal>
  );
};

export default BlacklistEntryDialog;
